import { useEffect, useState } from "react";
import { Download, ChevronDown, ChevronUp, MessageSquare } from "lucide-react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

const csvEscape = (val) => {
  const s = String(val ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const ChatSessionsManager = () => {
  const [sessions, setSessions] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchSessions = async () => {
    try {
      const { data } = await apiClient.get("/admin/chat-sessions");
      setSessions(data);
    } catch {
      toast.error("Could not load chat conversations");
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const toggleExpand = async (sessionId) => {
    if (expandedId === sessionId) {
      setExpandedId(null);
      setTranscript(null);
      return;
    }
    setExpandedId(sessionId);
    setTranscript(null);
    try {
      const { data } = await apiClient.get(`/admin/chat-sessions/${sessionId}/messages`);
      setTranscript(data);
    } catch {
      toast.error("Could not load transcript");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await apiClient.get("/admin/chat-sessions/export");
      const header = ["session_id", "role", "content", "created_at"];
      const rows = data.map((d) => [d.session_id, d.role, d.content, d.created_at]);
      const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fergusons-chat-transcripts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Transcripts exported");
    } catch {
      toast.error("Could not export transcripts");
    } finally {
      setExporting(false);
    }
  };

  const filtered = sessions
    ? sessions.filter(
        (s) =>
          !search.trim() ||
          s.session_id.toLowerCase().includes(search.toLowerCase()) ||
          s.preview.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div data-testid="admin-chat-sessions-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-[#A1B0A6] max-w-md">
          Browse visitor conversations with the Compliance Assistant to spot common questions.
        </p>
        <button
          onClick={handleExport}
          disabled={exporting || !sessions?.length}
          data-testid="admin-chat-export-btn"
          className="flex items-center gap-1.5 text-sm font-medium bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] rounded-full px-4 py-2 transition-colors shrink-0"
        >
          <Download size={14} /> {exporting ? "Exporting..." : "Export All CSV"}
        </button>
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by session ID or message content..."
        data-testid="admin-chat-search-input"
        className="w-full bg-deep-card border border-gold/15 rounded-md px-3 py-2 text-sm text-[#F4F7F5] placeholder:text-[#6E7E74] focus:outline-none focus:border-gold/50 mb-5"
      />

      {!sessions ? (
        <p className="text-[#A1B0A6] text-sm">Loading conversations...</p>
      ) : filtered.length === 0 ? (
        <p className="text-[#A1B0A6] text-sm" data-testid="admin-chat-empty">
          No conversations yet.
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => (
            <div
              key={s.session_id}
              data-testid={`admin-chat-session-row-${s.session_id}`}
              className="rounded-xl border border-gold/15 bg-deep-card overflow-hidden"
            >
              <button
                onClick={() => toggleExpand(s.session_id)}
                data-testid={`admin-chat-session-toggle-${s.session_id}`}
                className="w-full flex items-center justify-between gap-3 p-4 text-left"
              >
                <div className="min-w-0 flex items-start gap-3">
                  <MessageSquare size={15} className="text-gold mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-[#F4F7F5] truncate">{s.preview}</p>
                    <p className="text-xs text-[#6E7E74] mt-1">
                      {s.message_count} messages &middot; last active{" "}
                      {new Date(s.last_message_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                {expandedId === s.session_id ? (
                  <ChevronUp size={16} className="text-[#A1B0A6] shrink-0" />
                ) : (
                  <ChevronDown size={16} className="text-[#A1B0A6] shrink-0" />
                )}
              </button>

              {expandedId === s.session_id && (
                <div
                  data-testid={`admin-chat-transcript-${s.session_id}`}
                  className="border-t border-gold/10 p-4 space-y-2.5 bg-deep max-h-80 overflow-y-auto"
                >
                  {!transcript ? (
                    <p className="text-xs text-[#6E7E74]">Loading transcript...</p>
                  ) : (
                    transcript.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[80%] rounded-lg px-3 py-2 text-xs leading-relaxed ${
                            m.role === "user" ? "bg-gold text-[#0A140F]" : "bg-deep-card text-[#D7E0DA] border border-gold/10"
                          }`}
                        >
                          {m.content}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
